import { useMemo } from "react";
import Image from "next/image";
import axios from "axios";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { useQuery } from "@tanstack/react-query";
import { IntegrationType } from "@/utils/integrationConfig";
import {
  IntegrationsInfoResponse,
  ExternalLinkedAccountCard,
} from "../Views/LinkedAccounts";
import { RadioGroup, RadioGroupItem } from "../shadcn/radio-group";
import { Label } from "../shadcn/label";
import { cn } from "@/lib/utils";
import { Skeleton } from "../shadcn/skeleton";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { Info } from "lucide-react";
import { Toggle } from "./ToggleField";
import { useEditorReadOnly } from "@/hooks/useEditorReadOnly";

interface IntegrationPickerFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  tooltip?: string;
  integrations: string[];
  isDisabled?: boolean;
  returnTo?: string;
  hasMockedInfo?: boolean;
}

export const IntegrationPickerField = <T extends FieldValues>({
  name,
  control,
  label,
  tooltip,
  integrations,
  isDisabled = false,
  returnTo,
  hasMockedInfo = true,
}: IntegrationPickerFieldProps<T>) => {
  const { spotGroup } = useSpotUser();
  // In view-only editor mode the picker is disabled and the
  // integrations:edit-gated endpoints would 403. Skip both fetches.
  const isReadOnly = useEditorReadOnly();

  // Fetch integration status (for connected boolean)
  const { data: integrationsData, isLoading: loadingIntegrations } =
    useRemoteData(
      DATA_KEYS.INTEGRATIONS,
      {
        path: { group_id: spotGroup.id },
      },
      { enabled: !isReadOnly }
    );

  // Fetch integration info (new format — name, image, etc.)
  const { data: integrationsInfo, isLoading: loadingConfig } =
    useQuery<IntegrationsInfoResponse>({
      queryKey: [
        DATA_KEYS.INTEGRATIONS_CONFIG,
        { path: { group_id: spotGroup.id } },
      ],
      queryFn: async () => {
        const resp = await axios.get(
          `/api/backend/groups/${spotGroup.id}/integrations/config`
        );
        return resp.data;
      },
      enabled: !!spotGroup?.id && !isReadOnly,
    });

  // Build lookup dict keyed by integration type
  const INTEGRATIONS = useMemo(() => {
    const map: Record<string, { name: string; image: string }> = {};
    for (const i of integrationsInfo?.integrations ?? []) {
      map[i.type] = { name: i.display_name, image: i.image };
    }
    return map;
  }, [integrationsInfo]);

  const isIntegrationConnected = (integration: string): boolean => {
    if (!integrationsData) return false;
    return (
      integrationsData[integration as keyof typeof integrationsData] === true
    );
  };

  if (loadingIntegrations || loadingConfig) {
    return (
      <div className="space-y-4">
        {label && <Skeleton className="h-5 w-32" />}
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      </div>
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selected =
          (field.value as { integration?: string; is_mocked?: boolean }) ||
          undefined;
        const selectedIntegration = selected?.integration as string | undefined;
        const selectedIntegrationConfig = selectedIntegration
          ? INTEGRATIONS[selectedIntegration as IntegrationType]
          : undefined;
        const isSelectedConnected = selectedIntegration
          ? isIntegrationConnected(selectedIntegration)
          : false;

        return (
          <div className="space-y-4">
            {/* Integrations Section */}
            <div>
              {label && (
                <NoxusFormLabel label={label} tooltip={tooltip || ""} />
              )}
              <RadioGroup
                value={selectedIntegration}
                onValueChange={(value) => {
                  field.onChange({
                    integration: value,
                    is_mocked: selected?.is_mocked ?? false,
                  });
                }}
                className="grid grid-cols-3 gap-3 mt-2"
                disabled={isDisabled}
              >
                {integrations.map((integration) => {
                  const config = INTEGRATIONS[integration as IntegrationType];
                  if (!config) return null;

                  return (
                    <div
                      key={integration}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2 rounded-lg border transition-all duration-200",
                        selectedIntegration === integration
                          ? "border-secondary bg-accent/50"
                          : "border-border bg-background hover:bg-accent/30",
                        "cursor-pointer"
                      )}
                      onClick={() =>
                        !isDisabled &&
                        field.onChange({
                          integration,
                          is_mocked: selected?.is_mocked ?? false,
                        })
                      }
                    >
                      <RadioGroupItem
                        value={integration}
                        id={`${name}-${integration}`}
                        disabled={isDisabled}
                      />
                      <Label
                        htmlFor={`${name}-${integration}`}
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                      >
                        <div className="relative w-6 h-6">
                          <Image
                            fill
                            alt={config.name}
                            src={config.image}
                            style={{ objectFit: "contain" }}
                          />
                        </div>
                        <span className="font-medium text-sm">
                          {config.name}
                        </span>
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>

            {/* Setup Section - Only shown if an integration is selected and not connected */}
            {selectedIntegration &&
              !isSelectedConnected &&
              selectedIntegrationConfig && (
                <div>
                  <p className="text-sm font-medium mb-2">
                    Setup {selectedIntegrationConfig.name}
                  </p>
                  {hasMockedInfo && (
                    <div className="flex items-center justify-start gap-2 py-2 px-4 bg-gray-50 rounded-sm text-muted-foreground text-xs font-regular leading-normal mb-2">
                      <Info size={14} />{" "}
                      <span>
                        Appropriate demo data will be used in case you don't
                        connect the integration.
                      </span>
                    </div>
                  )}
                  <ExternalLinkedAccountCard
                    slug={selectedIntegration as IntegrationType}
                    onSuccess={() => {
                      // Integration connected, form value stays the same
                    }}
                    isDisabled={isDisabled}
                    returnTo={returnTo}
                  />
                </div>
              )}

            {isSelectedConnected && (
              <Toggle
                label="Demo Mode"
                description="Turn this on to test with demo data. When enabled, no data or actions will be sent to your connected account."
                value={!!selected?.is_mocked}
                onChange={(value: boolean) => {
                  field.onChange({
                    integration: selectedIntegration,
                    is_mocked: value,
                  });
                }}
                isDisabled={isDisabled}
              />
            )}
          </div>
        );
      }}
    />
  );
};
